import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../lib/formatters.js';
import { NotesField } from './NotesField.js';

interface Check {
  checkType: string;
  outcome: string;
  confidence: number;
  reasoning: string;
}

export interface PreVerifyResult {
  verdict: 'AUTO_APPROVABLE' | 'NEEDS_REVIEW' | 'INSUFFICIENT_EVIDENCE' | 'LIKELY_REJECT';
  confidence: number;
  reasoning: string;
  recommendedAmount: number | null;
  proposalId: string;
  checks: Check[];
}

interface AIVerificationModalProps {
  result: PreVerifyResult;
  isoCurrencyCode: string;
  notes: string;
  onNotesChange: (v: string) => void;
  onClose: () => void;
  onSubmit: (notes: string) => void;
  onAdjustToAmount: (amount: number) => void;
  onAddMoreEvidence: () => void;
  onSubmitAnyway: (justification: string, verdict: string) => void;
  submitting: boolean;
}

const VERDICT_COLORS: Record<string, string> = {
  AUTO_APPROVABLE: 'text-success border-success/30 bg-success/5',
  NEEDS_REVIEW: 'text-copper border-copper/30 bg-copper-soft',
  INSUFFICIENT_EVIDENCE: 'text-slate-600 border-slate-200 bg-slate-50',
  LIKELY_REJECT: 'text-danger border-danger/30 bg-danger/5',
};

const OUTCOME_COLORS: Record<string, string> = {
  PASS: 'text-success bg-success/10',
  WARN: 'text-copper bg-copper-soft',
  FAIL: 'text-danger bg-danger/10',
  INDETERMINATE: 'text-slate-500 bg-slate-100',
};

export function AIVerificationModal({
  result,
  isoCurrencyCode,
  notes,
  onNotesChange,
  onClose,
  onSubmit,
  onAdjustToAmount,
  onAddMoreEvidence,
  onSubmitAnyway,
  submitting,
}: AIVerificationModalProps) {
  const { t } = useTranslation();
  const [reasoningExpanded, setReasoningExpanded] = useState(false);
  const [showJustification, setShowJustification] = useState(false);
  const [justification, setJustification] = useState('');

  const pct = Math.round(result.confidence * 100);
  const { verdict, recommendedAmount } = result;
  const verdictColor = VERDICT_COLORS[verdict] ?? VERDICT_COLORS['NEEDS_REVIEW'];

  return (
    <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4">
      <div className="bg-bone-soft border border-hairline rounded w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-copper">
            {t('wizard.modal.title')}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-ink transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Verdict + confidence */}
          <div className={['p-4 border rounded', verdictColor].join(' ')}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-sm">
                {t(`wizard.verdicts.${verdict}`, { defaultValue: verdict })}
              </span>
              <span className="font-mono text-xs">{pct}%</span>
            </div>
            <div className="h-1.5 bg-black/10 rounded-sm overflow-hidden mt-2">
              <div
                className={['h-full rounded-sm', pct >= 85 ? 'bg-success' : pct >= 60 ? 'bg-copper' : 'bg-danger'].join(' ')}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Recommended amount (only for LIKELY_REJECT / INSUFFICIENT_EVIDENCE) */}
          {recommendedAmount != null && (verdict === 'LIKELY_REJECT' || verdict === 'INSUFFICIENT_EVIDENCE') && (
            <div className="px-4 py-3 border border-copper/30 bg-copper-soft rounded text-sm">
              <span className="text-slate-600">{t('wizard.labels.recommendedAmount')}: </span>
              <strong className="font-mono text-ink">{formatCurrency(recommendedAmount, isoCurrencyCode)}</strong>
            </div>
          )}

          {/* Reasoning — collapsible */}
          <div className="border border-hairline rounded overflow-hidden">
            <button
              type="button"
              onClick={() => setReasoningExpanded((p) => !p)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-bone hover:bg-bone-soft transition-colors text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              <span>{t('wizard.modal.reasoning')}</span>
              <span>{reasoningExpanded ? '▼' : '▶'}</span>
            </button>
            {reasoningExpanded && (
              <div className="px-4 py-3 text-sm text-slate-600 leading-relaxed border-t border-hairline bg-bone">
                {result.reasoning}
              </div>
            )}
          </div>

          {/* Verification checks */}
          {result.checks.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                {t('wizard.labels.verificationChecks')}
              </p>
              <div className="space-y-1.5">
                {result.checks.map((c, i) => (
                  <div key={i} className="flex items-start gap-3 px-3 py-2 border border-hairline rounded bg-bone">
                    <span className={['text-[10px] font-bold px-1.5 py-0.5 rounded-sm mt-0.5 shrink-0', OUTCOME_COLORS[c.outcome] ?? OUTCOME_COLORS['INDETERMINATE']].join(' ')}>
                      {t(`wizard.findings.outcomes.${c.outcome}`, { defaultValue: c.outcome })}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-ink">
                        {t(`wizard.findings.checkTypes.${c.checkType}`, { defaultValue: c.checkType })}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{c.reasoning}</p>
                    </div>
                    <span className="text-xs font-mono text-slate-400 shrink-0">
                      {Math.round(c.confidence * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes field */}
          <NotesField value={notes} onChange={onNotesChange} />

          {/* Submit-anyway justification panel */}
          {showJustification && (
            <div className="border border-copper/30 rounded p-4 space-y-3 bg-copper-soft">
              <p className="text-xs font-semibold text-copper-deep">{t('wizard.aiWarning.justificationLabel')}</p>
              <textarea
                rows={3}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder={t('wizard.aiWarning.placeholder')}
                className="w-full border border-hairline rounded px-3 py-2 text-sm bg-bone-soft focus:outline-none focus:border-copper resize-none"
              />
              <p className="text-xs text-slate-400">{t('wizard.aiWarning.charCount', { count: justification.length })}</p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowJustification(false); setJustification(''); }}
                  className="px-4 py-1.5 text-sm text-slate-600 hover:text-ink"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  disabled={justification.length < 30 || submitting}
                  onClick={() => onSubmitAnyway(justification, verdict)}
                  className="px-4 py-1.5 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm disabled:opacity-40 transition-colors"
                >
                  {submitting ? t('common.processing') : t('wizard.modal.submitAnywayConfirm')}
                </button>
              </div>
            </div>
          )}

          {/* Action bar */}
          {!showJustification && (
            <div className="space-y-3 pt-1">
              {/* AUTO_APPROVABLE / NEEDS_REVIEW */}
              {(verdict === 'AUTO_APPROVABLE' || verdict === 'NEEDS_REVIEW') && (
                <>
                  <button
                    type="button"
                    onClick={() => onSubmit(notes)}
                    disabled={submitting}
                    className="w-full px-5 py-2.5 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm disabled:opacity-40 transition-colors"
                  >
                    {submitting ? t('wizard.info.submitting') : t('wizard.actions.submitClaim')}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full px-5 py-2 border border-hairline text-slate-600 rounded text-sm hover:border-copper-warm transition-colors"
                  >
                    {t('wizard.modal.editClaim')}
                  </button>
                </>
              )}

              {/* LIKELY_REJECT */}
              {verdict === 'LIKELY_REJECT' && (
                <>
                  {recommendedAmount != null && (
                    <button
                      type="button"
                      onClick={() => onAdjustToAmount(recommendedAmount)}
                      className="w-full px-5 py-2.5 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors"
                    >
                      {t('wizard.modal.adjustTo', {
                        amount: formatCurrency(recommendedAmount, isoCurrencyCode),
                      })}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full px-5 py-2 border border-hairline text-slate-600 rounded text-sm hover:border-copper-warm transition-colors"
                  >
                    {t('wizard.modal.editClaim')}
                  </button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowJustification(true)}
                      className="text-xs text-slate-500 hover:text-copper underline transition-colors"
                    >
                      {t('wizard.modal.submitAnywayLink')}
                    </button>
                  </div>
                </>
              )}

              {/* INSUFFICIENT_EVIDENCE */}
              {verdict === 'INSUFFICIENT_EVIDENCE' && (
                <>
                  <button
                    type="button"
                    onClick={onAddMoreEvidence}
                    className="w-full px-5 py-2.5 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors"
                  >
                    {t('wizard.actions.addMoreEvidence')}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full px-5 py-2 border border-hairline text-slate-600 rounded text-sm hover:border-copper-warm transition-colors"
                  >
                    {t('wizard.modal.editClaim')}
                  </button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowJustification(true)}
                      className="text-xs text-slate-500 hover:text-copper underline transition-colors"
                    >
                      {t('wizard.modal.submitAnywayLink')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
